import { endOfDay, startOfDay, subDays } from 'date-fns'
import { acceptHMRUpdate, defineStore } from 'pinia'

import { useUserStore } from './user'

import { entityHeaders } from '@/helpers/api'
import { HttpError } from '@/helpers/errors'
import {
  parseGnavSnapshots,
  getLastGnavValue,
  getGnavValueAt,
} from '@/helpers/gnav'
import { calculatePnL } from '@/helpers/portfolio'
import { parseTransfersToDWS, type DWSByDate } from '@/helpers/transfers'

import PerformanceService from '@/services/statistics/performance'
import TransferService from '@/services/root/transfer'

import type { RequestOptions } from '@/services/api.service'
import type { DataPoint } from '@/types/services/common'
import type { Currency } from '@/types/services/global'
import type { Entity, NavDate, ValuationDates } from '@/types/services/root'
import type { BaseRootUser } from '@/types/services/root_user'

export interface FundGnavState {
  data: DataPoint[]
  currency: Currency | null
  dateFrom: string | null
  dateTo: string | null
  loading: boolean
}

export interface FundCumulativeTransfersState {
  data: DWSByDate
  currency: Currency | null
  loading: boolean
}

export interface FundFirstDepositState {
  date: Date | null
  loading: boolean
}

export interface FundPortfolioData {
  gnav: FundGnavState
  cumulativeTransfers: FundCumulativeTransfersState
  firstDeposit: FundFirstDepositState
  deposits: number
  withdrawals: number
}

export interface ClientPortfolioState {
  gnav: Record<string, FundGnavState>
  cumulativeTransfers: Record<string, FundCumulativeTransfersState>
  firstDeposit: Record<string, FundFirstDepositState>
  deposits: Record<string, number>
  withdrawals: Record<string, number>
}

export interface FetchGnavParams {
  entityId: string
  rootUserId: string
  fundProductId: string
  currency: Currency
  dateFrom: string
  dateTo: string
}

export interface FetchFirstDepositParams {
  entityId: string
  rootUserId: string
  clientEntityId: string
}

export interface PortfolioPnLSummary {
  portfolioValue: number | null
  pnl: number | null
  deposits: number
  withdrawals: number
}

export interface FundSummary {
  latestVisibleNavDate: Date | null
  nextVisibleNavDate: Date | null
  pnlSummary: PortfolioPnLSummary
  pnl: number | null
  isPnlAvailable: boolean
  visibleSnapshotCount: number
}

function buildPortfolioPnLSummary(
  portfolioValue: number | null,
  deposits: number,
  withdrawals: number
): PortfolioPnLSummary {
  return {
    portfolioValue,
    pnl:
      portfolioValue === null
        ? null
        : calculatePnL(portfolioValue, deposits, withdrawals),
    deposits,
    withdrawals,
  }
}

function parseNavDate(
  navDate: NavDate,
  entityId: string,
  userStore: ReturnType<typeof useUserStore>
): Date {
  return userStore.stringToEntityTzDate(`${navDate.date}T00:00:00Z`, entityId)
}

function parsePastDates(
  valuationDates: ValuationDates,
  entityId: string,
  userStore: ReturnType<typeof useUserStore>
): Date[] {
  return valuationDates.past.map((d) => parseNavDate(d, entityId, userStore))
}

export const useClientPortfolioStore = defineStore('client-portfolio', {
  state(): ClientPortfolioState {
    return {
      gnav: {},
      cumulativeTransfers: {},
      firstDeposit: {},
      deposits: {},
      withdrawals: {},
    }
  },

  actions: {
    clearEntityData(entityId: string) {
      this.$patch((state) => {
        delete state.gnav[entityId]
        delete state.cumulativeTransfers[entityId]
        delete state.firstDeposit[entityId]
        delete state.deposits[entityId]
        delete state.withdrawals[entityId]
      })
    },

    async fetchGnav(params: FetchGnavParams) {
      const {
        entityId,
        rootUserId,
        fundProductId,
        currency,
        dateFrom,
        dateTo,
      } = params
      const userStore = useUserStore()

      if (!entityId) {
        return
      }

      const existing = this.gnav[entityId]
      if (
        existing &&
        !existing.loading &&
        existing.currency?.id === currency.id &&
        existing.dateFrom === dateFrom &&
        existing.dateTo === dateTo
      ) {
        return
      }

      this.$patch((state) => {
        state.gnav[entityId] = {
          data: [],
          currency: currency,
          dateFrom,
          dateTo,
          loading: true,
        }
      })

      const requestOptions: RequestOptions = {
        headers: entityHeaders(entityId, rootUserId),
        allowConcurrentRequests: true,
      }

      try {
        const response = await PerformanceService.getClientGNAV(
          {
            currency_id: currency.id,
            fund_product_id: fundProductId,
            root_user_id: rootUserId,
            date_from: dateFrom,
            date_to: dateTo,
          },
          requestOptions
        )

        this.$patch((state) => {
          state.gnav[entityId].data = parseGnavSnapshots(
            response.data.data.snapshots || [],
            (date: Date) => userStore.dateWithTzOffset(date, entityId)
          )
          state.gnav[entityId].loading = false
        })
      } catch (err) {
        this.$patch((state) => {
          state.gnav[entityId].loading = false
        })
        throw new HttpError(err)
      }
    },

    async fetchClientGnav(
      entity: Entity,
      rootUser: BaseRootUser,
      currency: Currency
    ) {
      if (!entity.fund_product) {
        return
      }

      const userStore = useUserStore()
      const valuationDatesOnly =
        entity.fund_product.additional_settings?.valuation_dates_only === true
      const entityNow = userStore.dateWithTzOffset(new Date(), entity.id)

      let fromDate: Date
      let toDate: Date

      if (valuationDatesOnly) {
        // valuation_dates_only fund: no firstDeposit needed (BE filters gnav
        // to past[] anyway). Mark slot as resolved so loading aggregations
        // don't spin.
        this.$patch((state) => {
          state.firstDeposit[entity.id] = { date: null, loading: false }
        })

        const valuationDates = userStore.getEntityValuationDates(entity.id)
        const pastDates = valuationDates
          ? parsePastDates(valuationDates, entity.id, userStore)
          : []
        if (pastDates.length === 0) {
          // valuation_dates_only fund but BE has no NAV data yet (no fee strategy
          // or no closed period). Nothing to fetch — short-circuit with
          // an empty, non-loading gnav slot.
          this.$patch((state) => {
            state.gnav[entity.id] = {
              data: [],
              currency,
              dateFrom: null,
              dateTo: null,
              loading: false,
            }
          })
          return
        }
        fromDate = pastDates[0]
        toDate = pastDates[pastDates.length - 1]
      } else {
        await this.fetchFirstDeposit({
          entityId: entity.id,
          rootUserId: rootUser.id,
          clientEntityId: rootUser.entity_id,
        })
        const firstDepositDate = this.firstDeposit[entity.id]?.date ?? null
        fromDate = firstDepositDate ?? subDays(entityNow, 1)
        toDate = entityNow
      }

      const dateFrom = userStore.utcOpenFromDate(fromDate, entity)
      const dateTo = userStore.utcCloseFromDate(toDate, entity)

      if (!dateFrom || !dateTo) {
        return
      }

      await this.fetchGnav({
        entityId: entity.id,
        rootUserId: rootUser.id,
        fundProductId: entity.fund_product.id,
        currency,
        dateFrom,
        dateTo,
      })
    },

    async fetchClientTransfers(
      entity: Entity,
      rootUser: BaseRootUser,
      currency: Currency
    ) {
      if (!entity.fund_product) {
        return
      }

      const userStore = useUserStore()
      const entityId = entity.id

      const existing = this.cumulativeTransfers[entityId]
      if (
        existing &&
        !existing.loading &&
        existing.currency?.id === currency.id
      ) {
        return
      }

      this.$patch((state) => {
        state.cumulativeTransfers[entityId] = {
          data: {},
          currency: null,
          loading: true,
        }
      })

      const requestOptions: RequestOptions = {
        headers: entityHeaders(entityId, rootUser.id),
        allowConcurrentRequests: true,
      }

      try {
        const response = await TransferService.getClientTransfers(
          entity.fund_product.id,
          rootUser.entity_id,
          [currency.id],
          requestOptions
        )

        const { byDate, totals } = parseTransfersToDWS(
          response.data.data,
          (date: Date) => userStore.dateWithTzOffset(date, entityId)
        )

        this.$patch((state) => {
          state.cumulativeTransfers[entityId].data = byDate
          state.cumulativeTransfers[entityId].currency = currency
          state.deposits[entityId] = totals.deposits
          state.withdrawals[entityId] = Math.abs(totals.withdrawals)
          state.cumulativeTransfers[entityId].loading = false
        })
      } catch (err) {
        this.$patch((state) => {
          state.cumulativeTransfers[entityId].loading = false
        })
        throw new HttpError(err)
      }
    },

    async fetchFirstDeposit(
      params: FetchFirstDepositParams
    ): Promise<Date | null> {
      const { entityId, rootUserId, clientEntityId } = params

      const existing = this.firstDeposit[entityId]
      if (existing && !existing.loading) {
        return existing.date
      }

      const userStore = useUserStore()

      this.$patch((state) => {
        state.firstDeposit[entityId] = {
          date: null,
          loading: true,
        }
      })

      const requestOptions: RequestOptions = {
        headers: entityHeaders(entityId, rootUserId),
        allowConcurrentRequests: true,
      }

      try {
        const response = await TransferService.getFirstDepositDates(
          entityId,
          clientEntityId,
          false,
          requestOptions
        )

        const firstDeposits = response.data.data
        let firstDateStr: string | null = null

        firstDeposits.forEach((deposit) => {
          if (firstDateStr === null || deposit.date < firstDateStr) {
            firstDateStr = deposit.date
          }
        })

        let date: Date | null = null
        if (firstDateStr) {
          date = userStore.stringToEntityTzDate(firstDateStr, entityId)
        }

        this.$patch((state) => {
          state.firstDeposit[entityId].date = date
          state.firstDeposit[entityId].loading = false
        })

        return date
      } catch (err) {
        this.$patch((state) => {
          state.firstDeposit[entityId].loading = false
        })
        throw new HttpError(err)
      }
    },
  },

  getters: {
    getFundData(state) {
      return (entityId: string): FundPortfolioData => {
        const gnav = state.gnav[entityId]
        const cumulativeTransfers = state.cumulativeTransfers[entityId]
        const firstDeposit = state.firstDeposit[entityId]
        const deposits = state.deposits[entityId]
        const withdrawals = state.withdrawals[entityId]

        return {
          gnav: gnav || {
            data: [],
            currency: null,
            dateFrom: null,
            dateTo: null,
            loading: true,
          },
          cumulativeTransfers: cumulativeTransfers || {
            data: {},
            currency: null,
            loading: true,
          },
          firstDeposit: firstDeposit || { date: null, loading: true },
          deposits: deposits || 0,
          withdrawals: withdrawals || 0,
        }
      }
    },

    getPnLSummary() {
      return (entityId: string): PortfolioPnLSummary => {
        const fund = this.getFundData(entityId)
        const portfolioValue = getLastGnavValue(fund.gnav.data)

        return buildPortfolioPnLSummary(
          portfolioValue,
          fund.deposits,
          fund.withdrawals
        )
      }
    },

    getPnLSummaryOnDate() {
      return (entityId: string, date: Date): PortfolioPnLSummary => {
        const fund = this.getFundData(entityId)

        const portfolioValue = getGnavValueAt(
          fund.gnav.data,
          endOfDay(date).getTime()
        )

        const periodTransfers = this.getPeriodTransferTotals(
          entityId,
          new Date(0),
          date
        )

        return buildPortfolioPnLSummary(
          portfolioValue,
          periodTransfers.deposits,
          periodTransfers.withdrawals
        )
      }
    },

    getPeriodTransferTotals(state) {
      return (
        entityId: string,
        periodStart: Date,
        periodEnd: Date
      ): { deposits: number; withdrawals: number } => {
        const byDate = state.cumulativeTransfers[entityId]?.data ?? {}
        const periodStartTime = startOfDay(periodStart).getTime()
        const periodEndTime = endOfDay(periodEnd).getTime() - 999
        let deposits = 0
        let withdrawals = 0
        Object.entries(byDate).forEach(([dateTime, entry]) => {
          const time = Number(dateTime)
          if (time >= periodStartTime && time <= periodEndTime) {
            deposits += entry.deposits
            withdrawals += entry.withdrawals
          }
        })
        return { deposits, withdrawals: Math.abs(withdrawals) }
      }
    },

    getFundSummary() {
      return (entity: Entity | null): FundSummary => {
        const userStore = useUserStore()
        const empty: PortfolioPnLSummary = {
          portfolioValue: null,
          pnl: null,
          deposits: 0,
          withdrawals: 0,
        }

        if (!entity) {
          return {
            latestVisibleNavDate: null,
            nextVisibleNavDate: null,
            pnlSummary: empty,
            pnl: null,
            isPnlAvailable: false,
            visibleSnapshotCount: 0,
          }
        }

        // The fund's `valuation_dates_only` setting is the source of truth
        // for "valuation-only vs daily" — do NOT infer it from the presence
        // of a valuation_dates payload, because BE returns null whenever
        // there is no primary fee strategy yet (so a freshly-configured
        // valuation_dates_only fund would otherwise be misclassified as
        // daily).
        const valuationDatesOnly =
          entity.fund_product?.additional_settings?.valuation_dates_only ===
          true

        if (!valuationDatesOnly) {
          const pnlSummary = this.getPnLSummary(entity.id)
          return {
            latestVisibleNavDate: null,
            nextVisibleNavDate: null,
            pnlSummary,
            pnl: pnlSummary.pnl,
            isPnlAvailable: true,
            visibleSnapshotCount: 0,
          }
        }

        const valuationDates = userStore.getEntityValuationDates(entity.id)
        const pastDates = valuationDates
          ? parsePastDates(valuationDates, entity.id, userStore)
          : []
        const latestVisibleNavDate =
          pastDates.length > 0 ? pastDates[pastDates.length - 1] : null
        const nextVisibleNavDate = valuationDates
          ? parseNavDate(valuationDates.future, entity.id, userStore)
          : null

        const pnlSummary = latestVisibleNavDate
          ? this.getPnLSummaryOnDate(entity.id, latestVisibleNavDate)
          : empty
        const isPnlAvailable = pastDates.length >= 2

        return {
          latestVisibleNavDate,
          nextVisibleNavDate,
          pnlSummary,
          pnl: isPnlAvailable ? pnlSummary.pnl : null,
          isPnlAvailable,
          visibleSnapshotCount: pastDates.length,
        }
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useClientPortfolioStore, import.meta.hot)
  )
}
